import { z } from 'zod';
import {
  customerGroupTypesSchema,
  carrierGroupTypesSchema,
  partyRoleSchema,
  userRoleTypesSchema,
} from '../../common';
import { nameSchema, objectIdSchema } from '../../validation';

const freelancer_driver = userRoleTypesSchema.Enum.freelancer_driver;
const carrier_company_driver = userRoleTypesSchema.Enum.carrier_company_driver;
const business_customer = customerGroupTypesSchema.Enum.business_customer;
const individual_customer = customerGroupTypesSchema.Enum.individual_customer;
const carrier_company = carrierGroupTypesSchema.Enum.carrier_company;

/**
 * Wallet holder type discriminator.
 *
 * The `'org'` value is the relations-v2 norm — every Org-held wallet
 * (business_customer / carrier_company / broker / multi-role) lands as
 * `'org'` plus a `roles[]` array on `walletHolderSchema`. The legacy
 * single-enum values (`'business_customer'`, `'carrier_company'`,
 * `'individual_customer'`) survive only on existing wallet docs and on
 * driver wallets; they are scheduled for removal in Phase 2 of the
 * legacy-collection retirement once `holder.roles` is required.
 */
export const WalletHolderTypeSchema = z.enum([
  'org',
  business_customer,
  individual_customer,
  freelancer_driver,
  carrier_company_driver,
  carrier_company,
]);
export type WalletHolderType = z.infer<typeof WalletHolderTypeSchema>;

export const walletHolderSchema = z.object({
  /**
   * **THIS _id WILL NOT BE DUPLICATED IN THE `Wallet` Collection**
   *
   * Per holder type:
   *
   * - `org`                     = (`Org` collection) — relations-v2 norm
   * - `business_customer`       = (`Org` collection, was `Business`) — legacy
   * - `individual_customer`     = (`User` collection) `FOR Customers ONLY`
   * - `freelancer_driver`       = (`Driver` collection)
   * - `carrier_company_driver`  = (`Driver` collection)
   * - `carrier_company`         = (`Org` collection, was `Carrier`) — legacy
   */
  _id: objectIdSchema,
  /** name of the Holder
   * `freelancer_driver`        = the driver displayName
   * `carrier_company_driver`   = the driver displayName
   * `individual_customer`      = the customer display name
   * `org` / `business_customer` / `carrier_company` = the company display name
   */
  name: nameSchema,
  /**
   * the type of the wallet holder. See {@link WalletHolderTypeSchema} JSDoc
   * for what `'org'` means versus the legacy single-role values.
   */
  type: WalletHolderTypeSchema,

  /**
   * Network roles the holder plays — mirrors `Org.roles[]` for org-held
   * wallets. Optional during the relations-v2 transition because legacy
   * docs do not carry it; Phase 2 makes this required and drops the
   * legacy `business_customer` / `carrier_company` enum values from
   * {@link WalletHolderTypeSchema}.
   */
  roles: z.array(partyRoleSchema).optional(),

  /**
   * This id will always be coming form `User` collection:
   * - Individual Customer
   * - Freelancer Driver
   * - Carrier Company Driver.
   *
   * It's optional in case the Holder is an organization such as (carrier company, or business)
   */
  userId: objectIdSchema.optional(),
});
